import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  FaTimes,
  FaFileExcel,
  FaCheckSquare,
  FaRegSquare,
  FaFilter,
  FaUsers,
  FaCheckCircle,
  FaDownload,
} from 'react-icons/fa';
import { exportClientServicesToExcel } from '../../utils/excelExportUtils';

const SERVICE_OPTIONS = [
  { key: 'annualReport', label: 'Annual Report', desc: 'State, Filing Year, Annual Report Due Date & Filing Date' },
  { key: 'tax', label: 'Tax Filing', desc: 'Tax Form, Tax Year End Date, Tax Due Date, Filing Date & Steps' },
  { key: 'registeredAgent', label: 'Registered Agent', desc: 'Service Due Date, Registration Date & Reminders' },
  { key: 'bookkeeping', label: 'Bookkeeping', desc: 'Frequency, Start Date, Bookkeeping Due Date & Steps' },
  { key: 'usa', label: 'USA Company Formation', desc: 'Company Form, Formation Date / Due Date & Steps' },
  { key: 'cfo', label: 'CFO Advisory', desc: 'CFO Advisory client records' },
  { key: 'general', label: 'General Accounts', desc: 'Clients with no active services' },
];

const ExportExcelModal = ({
  isOpen,
  onClose,
  filteredUsers = [],
  allUsers = [],
  selectedIds = [],
  employees = [],
  serviceFilter = 'all',
}) => {
  const [clientScope, setClientScope] = useState('filtered');
  const [selectedServices, setSelectedServices] = useState({});
  const [includeMasterSheet, setIncludeMasterSheet] = useState(true);
  const [includeSeparateSheets, setIncludeSeparateSheets] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setClientScope(selectedIds.length > 0 ? 'selected' : 'filtered');

      // If a specific service filter is active on page, pre-select ONLY that service!
      if (serviceFilter && serviceFilter !== 'all') {
        const initial = SERVICE_OPTIONS.reduce((acc, item) => {
          acc[item.key] = item.key === serviceFilter;
          return acc;
        }, {});
        setSelectedServices(initial);
      } else {
        // Pre-select all
        const initial = SERVICE_OPTIONS.reduce((acc, item) => {
          acc[item.key] = true;
          return acc;
        }, {});
        setSelectedServices(initial);
      }
    }
  }, [isOpen, selectedIds.length, serviceFilter]);

  if (!isOpen) return null;

  const getClientCount = () => {
    if (clientScope === 'selected') return selectedIds.length;
    if (clientScope === 'filtered') return filteredUsers.length;
    return allUsers.length;
  };

  const selectedServicesCount = Object.values(selectedServices).filter(Boolean).length;
  const allServicesSelected = selectedServicesCount === SERVICE_OPTIONS.length;

  const toggleAllServices = () => {
    const newVal = !allServicesSelected;
    setSelectedServices(
      SERVICE_OPTIONS.reduce((acc, item) => ({ ...acc, [item.key]: newVal }), {})
    );
  };

  const selectSingleServiceOnly = (targetKey) => {
    setSelectedServices(
      SERVICE_OPTIONS.reduce((acc, item) => ({ ...acc, [item.key]: item.key === targetKey }), {})
    );
  };

  const toggleService = (key) => {
    setSelectedServices((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDownload = () => {
    let clientsToExport = [];
    if (clientScope === 'selected') {
      clientsToExport = allUsers.filter((u) => selectedIds.includes(u._id));
    } else if (clientScope === 'filtered') {
      clientsToExport = filteredUsers;
    } else {
      clientsToExport = allUsers;
    }

    if (clientsToExport.length === 0) {
      alert('No clients match your selected scope.');
      return;
    }

    if (selectedServicesCount === 0) {
      alert('Please select at least one service to export.');
      return;
    }

    const empMap = (employees || []).reduce((acc, emp) => {
      if (Array.isArray(emp.clients)) {
        emp.clients.forEach((c) => {
          if (c._id) acc[c._id] = emp.name;
        });
      }
      return acc;
    }, {});

    const activeKeys = Object.keys(selectedServices).filter((k) => selectedServices[k]);
    const serviceNameTag = activeKeys.length === 1 ? activeKeys[0] : 'custom';
    const dateStr = new Date().toISOString().slice(0, 10);
    const filename = `${serviceNameTag}_clients_${clientScope}_${dateStr}.xlsx`;

    exportClientServicesToExcel(clientsToExport, empMap, {
      filename,
      selectedServices,
      includeMasterSheet,
      includeSeparateSheets,
    });

    onClose();
  };

  return createPortal(
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="relative w-full max-w-xl bg-white dark:bg-dark-800 rounded-2xl shadow-2xl border border-gray-100 dark:border-dark-700 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-emerald-600 to-teal-700 text-white">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-white/20 backdrop-blur-md">
              <FaFileExcel className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Export Services to Excel</h3>
              <p className="text-xs text-emerald-100">Select specific service to download clean dedicated sheet</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-white/80 hover:text-white hover:bg-white/20 transition-colors"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Quick Preset Buttons */}
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
              Quick Single Service Download Presets:
            </label>
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => selectSingleServiceOnly('annualReport')}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                  selectedServicesCount === 1 && selectedServices.annualReport
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100'
                }`}
              >
                Annual Report Only
              </button>
              <button
                type="button"
                onClick={() => selectSingleServiceOnly('tax')}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                  selectedServicesCount === 1 && selectedServices.tax
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100'
                }`}
              >
                Tax Filing Only
              </button>
              <button
                type="button"
                onClick={() => selectSingleServiceOnly('registeredAgent')}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                  selectedServicesCount === 1 && selectedServices.registeredAgent
                    ? 'bg-purple-600 text-white shadow-md'
                    : 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-100'
                }`}
              >
                Registered Agent Only
              </button>
              <button
                type="button"
                onClick={() => selectSingleServiceOnly('bookkeeping')}
                className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                  selectedServicesCount === 1 && selectedServices.bookkeeping
                    ? 'bg-amber-600 text-white shadow-md'
                    : 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100'
                }`}
              >
                Bookkeeping Only
              </button>
              <button
                type="button"
                onClick={toggleAllServices}
                className="px-3 py-1 text-xs font-bold rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200"
              >
                {allServicesSelected ? 'Clear All' : 'Select All'}
              </button>
            </div>
          </div>

          {/* Section 1: Client Scope Selection */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
              <FaUsers className="w-3.5 h-3.5 text-emerald-600" />
              1. Choose Client Scope
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <button
                type="button"
                onClick={() => setClientScope('filtered')}
                className={`p-3 rounded-xl border text-left transition-all ${
                  clientScope === 'filtered'
                    ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20 ring-2 ring-emerald-500/30'
                    : 'border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-900 dark:text-white">Filtered Clients</span>
                  {clientScope === 'filtered' && <FaCheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                </div>
                <p className="mt-1 text-[11px] text-gray-500 font-semibold">{filteredUsers.length} Clients</p>
                <p className="text-[10px] text-gray-400">Matching page search & filters</p>
              </button>

              <button
                type="button"
                onClick={() => setClientScope('selected')}
                disabled={selectedIds.length === 0}
                className={`p-3 rounded-xl border text-left transition-all ${
                  selectedIds.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                  clientScope === 'selected'
                    ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20 ring-2 ring-emerald-500/30'
                    : 'border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-900 dark:text-white">Checked Clients</span>
                  {clientScope === 'selected' && <FaCheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                </div>
                <p className="mt-1 text-[11px] text-gray-500 font-semibold">{selectedIds.length} Checked</p>
                <p className="text-[10px] text-gray-400">Via table checkboxes</p>
              </button>

              <button
                type="button"
                onClick={() => setClientScope('all')}
                className={`p-3 rounded-xl border text-left transition-all ${
                  clientScope === 'all'
                    ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20 ring-2 ring-emerald-500/30'
                    : 'border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-900 dark:text-white">All Clients</span>
                  {clientScope === 'all' && <FaCheckCircle className="w-3.5 h-3.5 text-emerald-600" />}
                </div>
                <p className="mt-1 text-[11px] text-gray-500 font-semibold">{allUsers.length} Total</p>
                <p className="text-[10px] text-gray-400">Entire platform database</p>
              </button>
            </div>
          </div>

          {/* Section 2: Services Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider flex items-center gap-1.5">
                <FaFilter className="w-3.5 h-3.5 text-emerald-600" />
                2. Select Service ({selectedServicesCount} Selected)
              </label>
              {selectedServicesCount === 1 && (
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-full">
                  ✨ Single Clean Sheet Mode Active
                </span>
              )}
            </div>

            <div className="space-y-1.5 bg-gray-50 dark:bg-dark-700/40 p-3 rounded-xl border border-gray-200 dark:border-dark-600 max-h-56 overflow-y-auto">
              {SERVICE_OPTIONS.map((svc) => {
                const checked = Boolean(selectedServices[svc.key]);
                return (
                  <label
                    key={svc.key}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                      checked ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : 'hover:bg-white dark:hover:bg-dark-700'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => toggleService(svc.key)}
                      className="mt-0.5 text-emerald-600 hover:opacity-80"
                    >
                      {checked ? (
                        <FaCheckSquare className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <FaRegSquare className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                    <div className="flex-1" onClick={() => toggleService(svc.key)}>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-gray-900 dark:text-white">{svc.label}</span>
                        {svc.key === 'annualReport' && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-[9px] font-bold">
                            Includes Filing Year & State
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">{svc.desc}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Section 3: Formatting */}
          {selectedServicesCount > 1 && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
                3. Workbook Sheet Formatting
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="flex items-center gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeMasterSheet}
                    onChange={(e) => setIncludeMasterSheet(e.target.checked)}
                    className="rounded text-emerald-600 focus:ring-emerald-500 w-4 h-4"
                  />
                  <div>
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200">Combined Master Sheet</span>
                    <p className="text-[10px] text-gray-500">"All Selected Services" tab</p>
                  </div>
                </label>

                <label className="flex items-center gap-2.5 p-3 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50/50 dark:bg-dark-700/50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeSeparateSheets}
                    onChange={(e) => setIncludeSeparateSheets(e.target.checked)}
                    className="rounded text-emerald-600 focus:ring-emerald-500 w-4 h-4"
                  />
                  <div>
                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200">Individual Service Tabs</span>
                    <p className="text-[10px] text-gray-500">Separate tabs per service type</p>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 dark:bg-dark-700/50 border-t border-gray-200 dark:border-dark-700">
          <span className="text-xs text-gray-500">
            Exporting <strong className="text-emerald-600">{getClientCount()}</strong> clients & <strong className="text-emerald-600">{selectedServicesCount}</strong> service(s)
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-2 px-5 py-2.5 text-xs font-bold rounded-xl bg-emerald-600 text-white shadow-lg shadow-emerald-500/30 hover:bg-emerald-700 transition-all"
            >
              <FaDownload className="w-3.5 h-3.5" />
              Download Excel (.xlsx)
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExportExcelModal;
